import { GridColumns } from '@mui/x-data-grid';
import { startCase } from 'lodash';
import { saveAs } from 'file-saver';

import {
    createAutocompleteField,
    createTextField,
    createUseSearchService,
    DependenciesWatcher,
    ISearchConfiguration,
    ISearchRequest,
    ISearchResponse,
    ISearchService,
} from '../../../reusableFeatures';
import {
    backendServices,
    IUserGridItem,
    IUserSearchItems,
    orderBy,
    portalUserService,
    UserService,
} from '../../../services';
import { createUserFilter } from './User.filter';
import { PortalType } from '../../../types';

export class UsersSearchService implements ISearchService<IUserSearchItems> {
    private service = new UserService();

    private userApi = new backendServices.Apis.UserApi();

    constructor(
        private readonly portal: 'buyer' | 'supplier',
        private readonly dependenciesWatcher: DependenciesWatcher,
        private readonly roleTypes: backendServices.ViewModels.RoleTypeViewModel[]
    ) {}

    async fetchSearchConfiguration(): Promise<ISearchConfiguration<IUserSearchItems>> {
        await this.dependenciesWatcher.waitForDependencies();

        const buyerGridColumns: GridColumns<IUserGridItem> = [
            {
                field: 'lastSignIn',
                headerName: 'Last Sign-in',
                sortable: false,
                width: 150,
            },
            {
                field: 'substituteUserName',
                headerName: 'Substitute User',
                sortable: false,
                width: 200,
            },
        ];

        const supplierGridColumns: GridColumns<IUserGridItem> = [
            {
                field: 'lastSignIn',
                headerName: 'Last Sign-in',
                sortable: false,
                width: 150,
            },
        ];

        const defaultPageSize = 25;

        const gridColumns: GridColumns<IUserGridItem> = [
            {
                field: 'name',
                headerName: 'Name',
                sortable: true,
                width: 200,
            },
            {
                field: 'email',
                headerName: 'Email Address',
                sortable: true,
                valueGetter: (props: any) => props.row?.emailAddress,
                width: 200,
            },
            {
                field: 'roles',
                headerName: 'Roles',
                sortable: false,
                renderCell: ({ value }) => {
                    const roleIds = value.map((role: { id: number }) => role.id) as number[];

                    const addEllipsis = roleIds.length >= defaultPageSize;

                    const roleNames = roleIds
                        .map((id) => this.roleTypes.find((role) => role.ID === id)?.Name)
                        .filter(Boolean);

                    const startCaseRoleNames = roleNames.map((name) => startCase(name));

                    return startCaseRoleNames.join(', ').concat(addEllipsis ? '...' : '');
                },
            },
            {
                field: 'employeeNumber',
                headerName: 'Employee Number',
                sortable: true,
                width: 200,
            },
        ];

        if (this.portal === 'buyer') {
            gridColumns.push(...buyerGridColumns);
        }

        if (this.portal === 'supplier') {
            gridColumns.push(...supplierGridColumns);
        }

        const searchFormFieldColumns = [
            [
                createTextField({
                    id: 'name',
                    label: 'Name, Email',
                    defaultValue: '',
                    dataBindingKey: 'name',
                }),
                createAutocompleteField({
                    id: 'roleId',
                    label: 'Role',
                    defaultValue: null,
                    dataBindingKey: 'roleId',
                    options: this.roleTypes.map((role) => ({
                        label: startCase(role.Name),
                        value: role.ID,
                    })),
                }),
            ],
        ];

        return {
            gridColumns,
            enabledExportingMethods: [],
            searchFormFieldColumns,
            pageSizeOptions: [defaultPageSize, 50],
            defaultPageSize,
            searchWithNoCriteria: true,
        };
    }

    async fetchResults(
        _searchConfiguration: ISearchConfiguration<IUserSearchItems>,
        searchRequest: ISearchRequest<IUserSearchItems>
    ): Promise<ISearchResponse> {
        const companyID = portalUserService.getCurrentCompanyId();
        const { pageNumber, pageSize, sort, searchQuery } = searchRequest;
        const response = await this.service.getUsersSearchGrid(
            companyID,
            true,
            pageSize,
            pageNumber,
            orderBy(sort),
            createUserFilter({
                name: searchQuery.state.name,
                email: searchQuery.state.name,
                roleId: searchQuery.state.roleId ?? undefined,
            })
        );
        return {
            pageResults: response.items,
            totalResultCount: response.count,
        };
    }

    async exportData(
        _searchConfiguration: ISearchConfiguration<IUserSearchItems>,
        searchRequest: ISearchRequest<IUserSearchItems>,
        _exportMethod: string,
        filename: string
    ): Promise<void> {
        const companyID = portalUserService.getCurrentCompanyId();
        const { sort, searchQuery } = searchRequest;

        const portalTypes =
            this.portal === 'buyer'
                ? [PortalType.TransceptaManager, PortalType.TransceptaManagerAndSupplierPortal]
                : [PortalType.SupplierPortal, PortalType.TransceptaManagerAndSupplierPortal];

        const { data } = await this.userApi.getUsersCSV({
            companyId: companyID,
            includeSubstituteUser: true,
            $orderby: orderBy(sort),
            $filter: createUserFilter({
                name: searchQuery.state.name,
                email: searchQuery.state.name,
                roleId: searchQuery.state.roleId ?? undefined,
            }),
            portalType: portalTypes.join(','),
        });

        const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, filename);
    }

    get key(): string {
        const composedKey = ['UserSearch', this.dependenciesWatcher.state];
        return composedKey.join(' - ');
    }
}

export const useUsersSearchService = createUseSearchService(UsersSearchService);
